import React, { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faChevronDown,
	faChevronUp,
	faPlus,
	faSearch,
	faSync,
} from "@fortawesome/free-solid-svg-icons"
import {
	fetchInventaireCategories,
	fetchModalStock,
	fetchModalStockVirtuel,
	fetchModalStockVirtuelSuggestion,
} from "../../store/actions/atelierInventaireActions"
import AtelierInventaireFournitureModal from "./AtelierInventaireFournitureModal"
import CommandeHeader from "./inventaire/CommandeHeader"
import CustomModal from "../basics/interface/CustomModal"
import Loader from "../basics/Loader"
import { generalStyles } from "../styles/styles"
import { tableStyles as ts } from "../styles/tableStyles"
import { COLORS } from "../../utilities/Constants"
import { round100 } from "../../utilities/Addons"

// Prix : 2 décimales (laisse tel quel si non numérique).
const fmt2 = (v) => {
	const n = Number(v)
	return Number.isNaN(n) ? v : n.toFixed(2)
}

// Version V2 du modal d'inventaire — sélection d'articles (stock physique /
// stock virtuel / sous-traitants) à ajouter à la fiche.
//
// Pagination serveur + endless scroll : on ne rapatrie plus tout le stock d'un
// coup (≈10s en prod), mais page par page via les actions DÉDIÉES fetchModalStock
// / fetchModalStockVirtuel (champs reducer modalStock* / modalStockVirtuel*).
//
// Props : closeModal, inventaire, typeAl, plaques, addAtelierLigneInventaire,
// addAtelierLigneFourniture, addAtelierLigneSousTraitantAutre.

const STOCK_LIMIT = 30
const SV_LIMIT = 10

const viewport = { maxHeight: "56vh", overflowY: "auto", marginTop: 12 }
const sentinelStyle = {
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	gap: 8,
	padding: "14px 0",
	color: COLORS.textLight,
	fontSize: 12,
}

// Observe une sentinelle en bas d'une zone de scroll et déclenche `onMore` quand
// elle approche, tant qu'il reste des pages et qu'aucun chargement n'est en cours.
// `active` (onglet courant) est dans les deps : au retour sur un onglet, la zone
// est remontée (nouveau nœud DOM) et l'observer se ré-accroche au bon élément.
const useInfiniteScroll = (rootRef, sentinelRef, { active, hasMore, loading, onMore }) => {
	useEffect(() => {
		if (!active) return
		const root = rootRef.current
		const el = sentinelRef.current
		if (!root || !el) return
		const obs = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasMore && !loading) onMore()
			},
			{ root, rootMargin: "200px" },
		)
		obs.observe(el)
		return () => obs.disconnect()
	}, [active, rootRef, sentinelRef, hasMore, loading, onMore])
}

// --- Ligne de stock physique (hover + double-clic pour ajouter) ---------------
const StockRow = ({ article, index, onAdd }) => {
	const [hover, setHover] = useState(false)
	let qte = `${article.quantite}`
	if (article.quantite_reservee > 0) qte += ` (${article.quantite_reservee})`
	return (
		<tr
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			onDoubleClick={() => onAdd(article)}
			title="Double-cliquez pour ajouter"
			style={{
				backgroundColor: hover ? ts.rowHover : ts.rowBg(index),
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td style={ts.td}>{article.categorieNom}</td>
			<td style={{ ...ts.td, fontWeight: 600 }}>{article.nom}</td>
			<td style={ts.tdRight}>{Number(article.prixVente).toFixed(2)}</td>
			<td style={ts.td}>{qte}</td>
		</tr>
	)
}

// --- Ligne d'article d'une commande virtuelle (clic = sélection) --------------
// Mêmes colonnes que la carte stock virtuel de /atelier/inventaire (StockVirtuelCard).
const VirtRow = ({ article, index, selected, onToggle }) => {
	const [hover, setHover] = useState(false)
	const restante = article.quantite_recue - article.quantite_utilisee
	let restanteStr = `${restante}`
	if (article.quantite_reservee > 0)
		restanteStr += ` (${article.quantite_reservee})`
	const bg = selected
		? COLORS.accentLight
		: hover
			? ts.rowHover
			: ts.rowBg(index)
	return (
		<tr
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			onClick={() => onToggle(article)}
			title="Cliquez pour sélectionner"
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td
				style={{
					...ts.td,
					fontWeight: 600,
					color: selected ? COLORS.accent : COLORS.text,
				}}
			>
				{article.label}
			</td>
			<td style={ts.td}>{article.numero}</td>
			<td style={ts.td}>{article.quantite_recue}</td>
			<td style={ts.td}>{restanteStr}</td>
			<td style={ts.tdRight}>{fmt2(article.prix_achat_ht)}</td>
			<td style={ts.tdRight}>{fmt2(article.prix_vente_ht)}</td>
		</tr>
	)
}

// --- Carte d'une commande virtuelle (repliable, façon StockVirtuelCard) -------
// Réutilise CommandeHeader (pastilles date/réf/n°/total) pour coller exactement
// au rendu de /atelier/inventaire.
const VirtuelCmdCard = ({
	cmd,
	selectedIds,
	onToggle,
	onAddAll,
	defaultOpen,
	suggested,
}) => {
	const [open, setOpen] = useState(!!defaultOpen)
	const articles = cmd.articles || []
	const total = round100(
		articles.reduce((s, a) => s + a.quantite_recue * a.prix_achat_ht, 0),
	)
	return (
		<div
			style={{
				...ts.container,
				marginBottom: 12,
				...(suggested ? { border: `1px solid ${COLORS.accent}` } : {}),
			}}
		>
			{suggested && (
				<div
					style={{
						padding: "8px 18px 0",
						fontSize: 10,
						fontWeight: 600,
						letterSpacing: "0.06em",
						textTransform: "uppercase",
						color: COLORS.accent,
					}}
				>
					Commande suggérée
				</div>
			)}
			<CommandeHeader
				fournisseur={cmd.fournisseur_nom}
				date={cmd.date_commande}
				reference={cmd.reference}
				numero={cmd.numero_commande}
				total={total}
				actions={
					<>
						<button
							type="button"
							title="Tout ajouter"
							style={generalStyles.iconBtn(COLORS.accent)}
							onClick={() => onAddAll(cmd)}
						>
							<FontAwesomeIcon icon={faPlus} />
						</button>
						<button
							type="button"
							title={open ? "Réduire" : "Déplier"}
							style={generalStyles.iconBtn(COLORS.textMuted)}
							onClick={() => setOpen((o) => !o)}
						>
							<FontAwesomeIcon
								icon={open ? faChevronUp : faChevronDown}
							/>
						</button>
					</>
				}
			/>
			{open && (
				<table style={ts.table}>
					<thead>
						<tr>
							<th style={ts.th}>Article</th>
							<th style={ts.th}>Numéro</th>
							<th style={ts.th}>Qté reçue</th>
							<th style={ts.th}>Qté restante</th>
							<th style={ts.thRight}>Prix achat</th>
							<th style={ts.thRight}>Prix vente</th>
						</tr>
					</thead>
					<tbody>
						{articles.map((article, i) => (
							<VirtRow
								key={`sv_${article.id}_${i}`}
								article={article}
								index={i}
								selected={selectedIds.includes(article.id)}
								onToggle={onToggle}
							/>
						))}
					</tbody>
				</table>
			)}
		</div>
	)
}

const AtelierInventaireModalV2 = ({
	closeModal,
	inventaire,
	typeAl,
	plaques,
	addAtelierLigneInventaire,
	addAtelierLigneFourniture,
	addAtelierLigneSousTraitantAutre,
}) => {
	const dispatch = useDispatch()
	const [inventaireType, setInventaireType] = useState(0)
	const [categorieSelected, setCategorieSelected] = useState(0)
	const [search, setSearch] = useState("")
	const [debouncedSearch, setDebouncedSearch] = useState("")
	const [selectedStockVirtuels, setSelectedStockVirtuels] = useState([])

	const {
		categories,
		fetchCategories,
		modalStock,
		modalStockHasMore,
		modalStockLoadingMore,
		fetchModalStock: fetchModalStockState,
		modalStockVirtuel,
		modalStockVirtuelHasMore,
		modalStockVirtuelLoadingMore,
		fetchModalStockVirtuel: fetchModalStockVirtuelState,
		modalSuggestedCmds,
	} = inventaire

	const stockScrollRef = useRef(null)
	const stockSentinelRef = useRef(null)
	const svScrollRef = useRef(null)
	const svSentinelRef = useRef(null)
	const svInit = useRef(false)

	// Catégories (une fois).
	useEffect(() => {
		dispatch(fetchInventaireCategories())
	}, [dispatch])

	// Recherche debouncée.
	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(search.trim()), 300)
		return () => clearTimeout(t)
	}, [search])

	// Stock physique : (re)charge la 1re page à chaque changement de filtre.
	// S'exécute aussi au montage (filtres initiaux) → chargement initial du stock.
	useEffect(() => {
		const kwargs = { offset: 0, limit: STOCK_LIMIT }
		if (categorieSelected > 0) kwargs.categorie = categorieSelected
		if (debouncedSearch) kwargs.search = debouncedSearch
		dispatch(fetchModalStock(kwargs))
		if (stockScrollRef.current) stockScrollRef.current.scrollTop = 0
	}, [dispatch, categorieSelected, debouncedSearch])

	// Stock virtuel : chargé à la première ouverture de l'onglet (lazy).
	useEffect(() => {
		if (inventaireType !== 1 || svInit.current) return
		svInit.current = true
		dispatch(fetchModalStockVirtuel({ offset: 0, limit: SV_LIMIT }))
		if (plaques) dispatch(fetchModalStockVirtuelSuggestion(plaques))
	}, [inventaireType, dispatch, plaques])

	const loadMoreStock = useCallback(() => {
		const kwargs = { offset: modalStock.length, limit: STOCK_LIMIT }
		if (categorieSelected > 0) kwargs.categorie = categorieSelected
		if (debouncedSearch) kwargs.search = debouncedSearch
		dispatch(fetchModalStock(kwargs))
	}, [dispatch, modalStock.length, categorieSelected, debouncedSearch])

	const loadMoreSv = useCallback(() => {
		dispatch(
			fetchModalStockVirtuel({
				offset: modalStockVirtuel.length,
				limit: SV_LIMIT,
			}),
		)
	}, [dispatch, modalStockVirtuel.length])

	useInfiniteScroll(stockScrollRef, stockSentinelRef, {
		active: inventaireType === 0,
		hasMore: modalStockHasMore,
		loading: modalStockLoadingMore || fetchModalStockState.loading,
		onMore: loadMoreStock,
	})
	useInfiniteScroll(svScrollRef, svSentinelRef, {
		active: inventaireType === 1,
		hasMore: modalStockVirtuelHasMore,
		loading:
			modalStockVirtuelLoadingMore || fetchModalStockVirtuelState.loading,
		onMore: loadMoreSv,
	})

	const toggleVirt = (article) => {
		setSelectedStockVirtuels((sel) => {
			const i = sel.findIndex((a) => a.id === article.id)
			if (i === -1) return [...sel, article]
			return [...sel.slice(0, i), ...sel.slice(i + 1)]
		})
	}
	const selectedIds = selectedStockVirtuels.map((a) => a.id)

	const addAllFromCmd = (cmd) =>
		addAtelierLigneInventaire(
			(cmd.articles || []).filter(
				(art) => art.label !== "Depot" && art.numero !== "",
			),
		)

	const tabBtn = (idx, label) => (
		<button
			type="button"
			style={
				inventaireType === idx
					? generalStyles.btnPrimary
					: generalStyles.btnSecondary
			}
			onClick={() => setInventaireType(idx)}
		>
			{label}
		</button>
	)

	return (
		<CustomModal
			title="Inventaire"
			onClose={closeModal}
			showFooter={false}
			/* Onglet sous-traitants = contenu étroit (select + 1 colonne) → on
			   resserre le modal pour éviter l'effet « trop large ». */
			maxWidth={inventaireType === 2 ? 600 : 1120}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					gap: 8,
					marginBottom: 8,
				}}
			>
				{tabBtn(0, "Stock")}
				{tabBtn(1, "Stock virtuel")}
				{tabBtn(2, "Sous-traitants")}
			</div>

			{inventaireType === 0 ? (
				<>
					{/* Toolbar : catégorie + recherche */}
					<div
						style={{
							display: "flex",
							gap: 10,
							alignItems: "center",
							flexWrap: "wrap",
						}}
					>
						<div
							style={{
								...generalStyles.fieldRow,
								flex: "0 0 auto",
							}}
						>
							<label
								style={{
									...generalStyles.label,
									minWidth: "auto",
								}}
							>
								Catégorie
							</label>
							{fetchCategories.loading ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								<select
									style={{
										...generalStyles.select,
										minWidth: 180,
									}}
									value={categorieSelected}
									onChange={(e) =>
										setCategorieSelected(
											parseInt(e.target.value),
										)
									}
								>
									<option value={0}>Toutes</option>
									{categories.map((c) => (
										<option key={`selCat${c.id}`} value={c.id}>
											{c.nom}
										</option>
									))}
								</select>
							)}
						</div>
						<div
							style={{
								position: "relative",
								flex: 1,
								minWidth: 180,
							}}
						>
							<FontAwesomeIcon
								icon={faSearch}
								style={{
									position: "absolute",
									left: 12,
									top: "50%",
									transform: "translateY(-50%)",
									color: COLORS.textLight,
									fontSize: 13,
								}}
							/>
							<input
								style={{
									...generalStyles.input,
									width: "100%",
									paddingLeft: 34,
								}}
								placeholder="Rechercher un article…"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
						</div>
					</div>

					{fetchModalStockState.loading ? (
						<Loader size={28} padding={24} />
					) : (
						<div ref={stockScrollRef} style={viewport}>
							<div style={ts.container}>
								<table style={ts.table}>
									<thead>
										<tr>
											<th style={ts.th}>Catégorie</th>
											<th style={ts.th}>Article</th>
											<th style={ts.thRight}>
												Prix vente
											</th>
											<th style={ts.th}>
												Quantité (réservé)
											</th>
										</tr>
									</thead>
									<tbody>
										{modalStock.map((article, i) => (
											<StockRow
												key={`modalStock_${article.id}`}
												article={article}
												index={i}
												onAdd={addAtelierLigneInventaire}
											/>
										))}
									</tbody>
								</table>
								{modalStock.length === 0 && (
									<div style={ts.empty}>
										Aucun article trouvé.
									</div>
								)}
							</div>
							<div ref={stockSentinelRef} style={sentinelStyle}>
								{modalStockLoadingMore ? (
									<FontAwesomeIcon icon={faSync} spin />
								) : !modalStockHasMore && modalStock.length > 0 ? (
									"Fin de la liste"
								) : null}
							</div>
						</div>
					)}
					<div
						style={{
							color: COLORS.textMuted,
							fontSize: 12,
							marginTop: 8,
						}}
					>
						Double-cliquez sur un article pour l'ajouter.
					</div>
				</>
			) : inventaireType === 1 ? (
				fetchModalStockVirtuelState.loading &&
				modalStockVirtuel.length === 0 ? (
					<Loader size={28} padding={24} />
				) : (
					<>
						{selectedStockVirtuels.length > 0 && (
							<button
								type="button"
								style={{
									...generalStyles.btnPrimary,
									marginBottom: 4,
								}}
								onClick={() =>
									addAtelierLigneInventaire(
										selectedStockVirtuels,
									)
								}
							>
								Importer {selectedStockVirtuels.length} article
								{selectedStockVirtuels.length > 1 ? "s" : ""}
							</button>
						)}
						<div ref={svScrollRef} style={viewport}>
							{modalSuggestedCmds.map((cmd) => (
								<VirtuelCmdCard
									key={`sugg_${cmd.id}`}
									cmd={cmd}
									suggested
									defaultOpen
									selectedIds={selectedIds}
									onToggle={toggleVirt}
									onAddAll={addAllFromCmd}
								/>
							))}
							{modalStockVirtuel.map((cmd) => (
								<VirtuelCmdCard
									key={`cmd_${cmd.id}`}
									cmd={cmd}
									selectedIds={selectedIds}
									onToggle={toggleVirt}
									onAddAll={addAllFromCmd}
								/>
							))}
							{modalStockVirtuel.length === 0 &&
								modalSuggestedCmds.length === 0 && (
									<div style={ts.empty}>
										Aucune commande virtuelle.
									</div>
								)}
							<div ref={svSentinelRef} style={sentinelStyle}>
								{modalStockVirtuelLoadingMore ? (
									<FontAwesomeIcon icon={faSync} spin />
								) : !modalStockVirtuelHasMore &&
								  modalStockVirtuel.length > 0 ? (
									"Fin de la liste"
								) : null}
							</div>
						</div>
					</>
				)
			) : (
				<AtelierInventaireFournitureModal
					addAtelierLigneFourniture={addAtelierLigneFourniture}
					addAtelierLigneSousTraitantAutre={
						addAtelierLigneSousTraitantAutre
					}
					typeAl={typeAl}
					closeModal={closeModal}
				/>
			)}
		</CustomModal>
	)
}

export default AtelierInventaireModalV2
